/*
* Created on Jan 22, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import React, {useState, useEffect, useRef} from 'react';
import { useTranslation } from "react-i18next";
import {DEFAULT_SIZE_LIST, ORGANIZATION_NAME_FORMAT} from "../../service/common";
import ic_content_settings from '../../static/img/content/ic-content-settings.svg';
import ic_status_check_svg from '../../static/img/utils/ic-status-checked.svg';
import SettingsAvatarModal from "./SettingsAvatarModal";
import {useQuery} from "@tanstack/react-query";
import {useOutletContext, useParams} from "react-router-dom";
import MainContentPlaceholderComponent from "../main-content/MainContentPlaceholderComponent";
import {useAuthSession} from "../../context/AuthSessionContext";
import TextInputComponent from "../common/Form/TextInputComponent";
import SelectInputComponent from "../common/Form/SelectInputComponent";
import {useAlertContext} from "../../context/AlertContext";
import SubmitButtonComponent from "../common/Form/SubmitButtonComponent";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useOrganization} from "../../context/OrganizationContext";
import {useRegion} from "../../context/RegionContext";
import CountrySelectComponent from "../common/Form/CountrySelectComponent";
import useEmailChangedHook from "../../hook/EmailChangedHook";
import TextAreaComponent from "../common/Form/TextAreaComponent";
import SettingsAuthentication from "./settingsAuthentication";
import OrgContentPlaceholderComponent from "../organization-content/OrgContentPlaceholderComponent";


const init_input = {
    name: {value: '', error: null, required: true, init_value: ''},
    description: {value: '', error: null, required: false, init_value: ''},
    country: {value: '', error: null, required: true, init_value: ''},
    size: {value: '', error: null, required: false, init_value: ''},
    website: {value: '', error: null, required: false, init_value: ''},
    phone: {value: '', error: null, required: false, init_value: ''},
    avatars: {value: '', error: null, required: false, init_value: ''}
}


const SettingsIndexComponent = () => {
    const {region} = useRegion();
    const {userPermissions} = useOutletContext();
    const hasPermission = userPermissions.hasPermission('manage_organization_settings');
    const {OrganizationAPI} = useOrganizationApi();
    const {useCheckSession} = useAuthSession();
    const {oid} = useParams();
    const {t} = useTranslation();
    const [organization, setOrganization] = useState();
    const [input, setInput] = useState(init_input);
    const [refresh, setRefresh] = useState(1);
    const alert = useAlertContext();
    const current_date = new Date().toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'});
    const user = JSON.parse(localStorage.getItem('currentUser'));
    const default_description = t('@Created by {{display_name}} on {{date_time}}@', {display_name: user.display_name, date_time: current_date});
    const buttonRef = useRef();
    const {EmailWarningDialog} = useEmailChangedHook();
    const { data, isSuccess } = useQuery({
        queryKey: [`organization/${oid}`, refresh],
        queryFn: async () => {
            const org = await OrganizationAPI.get(`organization/${oid}`);
            return org.result;
        }
    });

    const [openAvatarModal, setOpenAvatarModal] = useState(false);
    // useEffect(()=> {
    //     checkSession();
    // }, [])

    useCheckSession();

    useEffect(()=>{
        if (data) {
            setOrganization(data);
            setInput(prevInput=>{
                prevInput.name.value = data.name;
                prevInput.name.init_value = data.name;
                prevInput.description.value = data.description;
                prevInput.description.init_value = data.description;
                prevInput.country.value = data.country;
                prevInput.country.init_value = data.country;
                prevInput.size.value = data.size;
                prevInput.size.init_value = data.size;
                prevInput.website.value = data.website;
                prevInput.website.init_value = data.website;
                prevInput.phone.value = data.phone;
                prevInput.phone.init_value = data.phone;
                prevInput.avatars.value = data.avatars;
                prevInput.avatars.init_value = data.avatars;
                return {...prevInput}
            });
        }

    }, [data]);

    const saveOrganization = async () => {
        const data_input = {};
        Object.keys(input).forEach(key=>{
            data_input[key] = input[key].value;
        });
        try {
            await OrganizationAPI.put(`organization/${organization.organization_id}`, null, data_input);
            alert(true, 'success', 0, t('@Success@'));
        } catch (err) {
            alert(true, 'error', err.code, err.message);
        }
        console.log(data_input);
        setOrganization((prev)=> {return {...prev, ...data_input}});
        setRefresh((prev)=> prev+1);
    }

    const onCloseAvatar = () => setOpenAvatarModal(false);

    const uploadAvatar = async (formData) => {
        const result = await OrganizationAPI.upload(`organization/${organization.organization_id}/avatar`, formData)
        console.log(result);
        setOpenAvatarModal(false);
        setRefresh((prev)=> prev+1);
    }

    const removeAvatar = async () => {
        await OrganizationAPI.delete(`organization/${organization.organization_id}/avatar`);
        setOrganization((prev)=> {
            delete prev['avatars'];
            return {...prev};
        });
    }

    const onCopy = async (e) => {
        const b = buttonRef.current;
        b.classList.add('copy-anime');
        b.getElementsByTagName('span')[0].innerHTML = t('@Copied@');
        await navigator.clipboard.writeText(organization.hash_code);
        setTimeout(function() {
            b.classList.remove('copy-anime');
            b.getElementsByTagName('span')[0].innerHTML='Copy';
            }, 1000);
    }

    return (
        <>
        {!input && <MainContentPlaceholderComponent />}
        {input &&
            <div className="content content-page">
                <div className="content-title">
                    <div className="title-ic"><img src={ic_content_settings} alt=""/></div>
                    <div className="title-label">{t('@Organization Profile@')}</div>
                </div>
                <div className="content-wrapper page-wrapper !pl-0 !pr-0">
                    <div className="content-inner">
                        <div className="content-col-left">
                            <div className={`profile-avatar ${organization?.avatars ? '' : 'no-avatar'}`}>
                                {organization?.avatars ? <img src={`${organization.avatars.medium}?t=${refresh}`} alt=""/> :
                                    <span>{organization?.name[0]}</span>}
                            </div>
                            {hasPermission &&
                                <div className="avatar-upload">
                                    <button type="button"
                                            className="q-btn q-btn-default q-btn-round-corner m--q-btn-upload"
                                            onClick={() => setOpenAvatarModal(true)}>
                                        {organization?.avatars ? `${t('@Edit@')} ${t('@Photo@')}` : t('@Upload photo@')}
                                    </button>
                                    <div className={`comp-dropdown comp-select-dropdown ${organization?.avatars ? '' : 'hide'}`}>
                                        <ul className="comp-dropdown-menu">
                                            <li className="section">
                                                <div className="section-items selection"
                                                     onClick={() => setOpenAvatarModal(true)}>{t('@New Photo@')}
                                                </div>
                                                <div className="section-items selection"
                                                     onClick={async () => await removeAvatar()}>{t('@Remove Photo@')}
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className="content-col-right">
                            <div className="form-group-wrapper mt-24">
                                <div className="form-group">
                                    <div className="form-group-inner">
                                        <TextInputComponent label={t('@Organization name@')} field_name={'name'}
                                                            input={input} setInput={setInput}
                                                            place_holder={t('@Please input your organization name@')}
                                                            disabled={!hasPermission} format={ORGANIZATION_NAME_FORMAT}
                                                            foramtError={t('@Punctuation marks cannot be used in the first character@')}/>
                                        {/*<SelectInputComponent label={region==='CHINA' ? t('@Location@') :t('@Country@')} field_name={'country'} input={input} setInput={setInput} listData={region==='CHINA' ? DEFAULT_COUNTRY_CHINA_LIST : DEFAULT_COUNTRY_LIST} useLocale={true} empty_text={t('@Please select your organization country@')} disabled={!is_owner} useSearch={true}/>*/}
                                        <CountrySelectComponent field_name={'country'} input={input} setInput={setInput}
                                                                useLocale={true}
                                                                empty_text={t('@Please select your organization country@')}
                                                                disabled={!hasPermission} useSearch={true}/>
                                        <SelectInputComponent label={t('@Organization size@')} field_name={'size'}
                                                              input={input} setInput={setInput}
                                                              listData={DEFAULT_SIZE_LIST}
                                                              useLocale={false}
                                                              empty_text={t('@Please select a approximate number of members in your organization@')}
                                                              disabled={!hasPermission}/>
                                        <TextInputComponent label={t('@Organization website@')} field_name={'website'}
                                                            input={input} setInput={setInput}
                                                            place_holder={t('@Please provide official website of your organization@')}
                                                            disabled={!hasPermission}/>
                                        <TextInputComponent label={t('@Organization phone number@')}
                                                            field_name={'phone'}
                                                            input={input} setInput={setInput}
                                                            place_holder={t('@Please provide contact number of your organization@')}
                                                            disabled={!hasPermission}/>
                                        <TextAreaComponent label={t('@Organization Description@')}
                                                           field_name={'description'} input={input} setInput={setInput}
                                                           place_holder={default_description} disabled={!hasPermission}/>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                { hasPermission &&
                    <div className="content-footer">
                        <div className="q-btn-group">
                            <SubmitButtonComponent text={t('@Update@')} input={input} onSubmit={saveOrganization}/>
                        </div>
                    </div>
                }

                {openAvatarModal && <SettingsAvatarModal isOpen={openAvatarModal} onClose={onCloseAvatar}
                                                         onSubmit={async (formData) => await uploadAvatar(formData)}
                                                         title={t('@Organization Avatar@')}/>}
            </div>
        }
            {isSuccess && <EmailWarningDialog/>}
        </>
    );
};

export default SettingsIndexComponent;